/**
 * Header - White background; logo and menu aligned to same horizontal start; nav colors per image.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Toolbar,
  Typography,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Divider,
  Collapse,
  Popper,
  Paper,
  ClickAwayListener,
} from '@mui/material';
import { ShoppingBag, Fastfood, AutoAwesome, RocketLaunch, KeyboardArrowDown, ExpandLess, ExpandMore } from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { ASSETS } from '../utils/assets';
import { getCampaignPath } from '../utils/campaignUrl';
import { campaignService } from '../services/api';

// Figma: menu bar and bottom strip left
const MENU_GREEN = '#477C2E';
// Figma: Donate Now and bottom strip right
const DONATE_BROWN = '#8A4B1E';
// Figma: "&" in ROOTS & WINGS
const AMPERSAND_COLOR = '#8A4B1E';
// Figma: vertical separator
const SEPARATOR_GRAY = '#ADADAD';

const navLinks = [
  { label: 'Home', to: '/' },
  {
    label: 'About',
    to: '/about',
    children: [
      { label: 'About', to: '/about' },
      { label: 'Founders', to: '/founders' },
      { label: 'Team', to: '/team' },
      { label: 'Financial & Legal Documents', to: '/financial-legal-documents' },
    ],
  },
  { label: 'Campaign', to: '/campaigns' },
  { label: 'Gallery', to: '/gallery' },
  { label: 'Volunteer', to: '/volunteer' },
  { label: 'Contact', to: '/contact' },
];

const HELPLINE = '9420249494';
const EMAIL = 'info@rwfngo.com';

function StartCampaignHeaderCTA({ compact = false }) {
  return (
    <Box
      component={RouterLink}
      to="/campaigns/submit"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: compact ? 1 : 1.25,
        textDecoration: 'none',
        flexShrink: 0,
        px: compact ? { xs: 1.5, sm: 2 } : { xs: 1.75, sm: 2.25 },
        py: compact ? 0.85 : 1.1,
        borderRadius: '999px',
        background: `linear-gradient(135deg, ${MENU_GREEN} 0%, #3d6a28 52%, #2f5520 100%)`,
        boxShadow: '0 10px 28px rgba(71, 124, 46, 0.32), inset 0 1px 0 rgba(255,255,255,0.22)',
        border: '1px solid rgba(255,255,255,0.28)',
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 0.22s ease, box-shadow 0.22s ease',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(105deg, rgba(255,255,255,0.18) 0%, transparent 45%)',
          pointerEvents: 'none',
        },
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 14px 36px rgba(71, 124, 46, 0.4), inset 0 1px 0 rgba(255,255,255,0.3)',
        },
        '&:active': { transform: 'translateY(0)' },
      }}
    >
      <Box
        sx={{
          width: compact ? 32 : 38,
          height: compact ? 32 : 38,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          bgcolor: 'rgba(255,255,255,0.2)',
          boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <RocketLaunch sx={{ color: '#fff', fontSize: compact ? 18 : 21 }} />
      </Box>
      <Box sx={{ position: 'relative', zIndex: 1, pr: compact ? 0.25 : 0.5 }}>
        <Typography
          sx={{
            fontFamily: '"Poppins", sans-serif',
            fontWeight: 700,
            fontSize: compact ? { xs: 11.5, sm: 12.5 } : { xs: 12.5, sm: 13.5 },
            lineHeight: 1.2,
            color: '#fff',
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap',
          }}
        >
          Start a Campaign
        </Typography>
        {!compact && (
          <Typography
            sx={{
              fontFamily: '"Poppins", sans-serif',
              fontWeight: 500,
              fontSize: { xs: 9.5, sm: 10 },
              lineHeight: 1.2,
              color: 'rgba(255,255,255,0.88)',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              mt: 0.2,
            }}
          >
            Submit for review
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function FeaturedNavLink({ campaign }) {
  const isSchool = campaign.variant === 'school_kit';
  const label =
    campaign.title.length > 24 ? `${campaign.title.slice(0, 24).trim()}…` : campaign.title;
  const iconBg = isSchool
    ? 'linear-gradient(145deg, rgba(232, 255, 240, 0.95) 0%, rgba(200, 235, 215, 0.85) 100%)'
    : 'linear-gradient(145deg, rgba(255, 243, 224, 0.98) 0%, rgba(255, 214, 165, 0.9) 100%)';
  const iconColor = isSchool ? '#1b5e3a' : '#9a3412';

  return (
    <Box
      component={RouterLink}
      to={getCampaignPath(campaign.slug)}
      sx={{
        ml: 1.25,
        pl: 0.65,
        pr: 1.35,
        py: 0.45,
        borderRadius: '999px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        maxWidth: { md: 200, lg: 220 },
        textDecoration: 'none',
        color: '#fff',
        flexShrink: 0,
        background:
          'linear-gradient(135deg, rgba(255,255,255,0.26) 0%, rgba(255,255,255,0.08) 100%)',
        border: '1px solid rgba(255,255,255,0.38)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.14), inset 0 1px 0 rgba(255,255,255,0.35)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        transition: 'transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease, background 0.22s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.45)',
          borderColor: 'rgba(255,255,255,0.55)',
          background:
            'linear-gradient(135deg, rgba(255,255,255,0.36) 0%, rgba(255,255,255,0.14) 100%)',
        },
        '&:active': { transform: 'translateY(0)' },
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          background: iconBg,
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        }}
      >
        {isSchool ? (
          <ShoppingBag sx={{ fontSize: 17, color: iconColor }} />
        ) : (
          <Fastfood sx={{ fontSize: 17, color: iconColor }} />
        )}
      </Box>
      <Box sx={{ minWidth: 0, textAlign: 'left' }}>
        <Typography
          component="span"
          sx={{
            display: 'block',
            fontFamily: '"Poppins", sans-serif',
            fontSize: 9,
            fontWeight: 600,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.88)',
            lineHeight: 1.2,
          }}
        >
          Featured
        </Typography>
        <Typography
          component="span"
          sx={{
            display: 'block',
            fontFamily: '"Poppins", sans-serif',
            fontWeight: 600,
            fontSize: { md: 11.5, lg: 12.5 },
            lineHeight: 1.25,
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            mt: 0.15,
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const aboutRef = useRef(null);
  const aboutCloseTimer = useRef(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [aboutMobileOpen, setAboutMobileOpen] = useState(false);
  const [featuredCampaigns, setFeaturedCampaigns] = useState([]);

  const openAboutMenu = () => {
    if (aboutCloseTimer.current) clearTimeout(aboutCloseTimer.current);
    setAboutOpen(true);
  };

  const closeAboutMenu = (immediate = false) => {
    if (aboutCloseTimer.current) clearTimeout(aboutCloseTimer.current);
    if (immediate) {
      setAboutOpen(false);
      return;
    }
    aboutCloseTimer.current = setTimeout(() => setAboutOpen(false), 180);
  };

  useEffect(() => {
    setAboutOpen(false);
  }, [location.pathname]);

  useEffect(() => () => {
    if (aboutCloseTimer.current) clearTimeout(aboutCloseTimer.current);
  }, []);

  const handleDonate = () => navigate('/campaigns');

  useEffect(() => {
    const loadFeaturedCampaigns = async () => {
      try {
        const res = await campaignService.getAllCampaigns({ status: 'active' });
        const payload = res.data?.data ?? res.data ?? [];
        const campaigns = Array.isArray(payload) ? payload : [];
        const sorted = campaigns
          .slice()
          .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());

        const latestHighlights = sorted
          .filter((c) => c?.slug && (c.donation_variant === 'school_kit' || c.donation_variant === 'freedom_hunger'))
          .slice(0, 2)
          .map((c) => ({
            id: c.id,
            slug: c.slug,
            title: c.title || 'Campaign',
            variant: c.donation_variant,
          }));

        setFeaturedCampaigns(latestHighlights);
      } catch (e) {
        console.error('Failed to load featured campaigns for menu:', e);
        setFeaturedCampaigns([]);
      }
    };
    loadFeaturedCampaigns();
  }, []);

  return (
    <>
      {/* Figma: light grey header; logo 47×50; ROOTS & WINGS (Manrope 800 17px); Foundation (Manrope 300 11px); dashed separator; Helpline/email block — hidden on mobile */}
      <Box sx={{ backgroundColor: '#F8F8F8', borderBottom: 'none' }}>
        <Box sx={{ width: '100%', px: 0 }}>
          {/* Mobile only: logo + ROOTS & WINGS / Foundation centered at top */}
          <Box
            sx={{
              display: { xs: 'flex', sm: 'none' },
              justifyContent: 'center',
              alignItems: 'center',
              py: 1.5,
              backgroundColor: '#F8F8F8',
            }}
          >
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              <Box
                component="img"
                src={ASSETS.logoJpeg}
                alt="Roots & Wings"
                sx={{ height: 44, width: 'auto', objectFit: 'contain', display: 'block', flexShrink: 0 }}
              />
              <Box sx={{ textAlign: 'left' }}>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: '"Manrope", sans-serif',
                    fontWeight: 800,
                    fontSize: 16,
                    lineHeight: '100%',
                    letterSpacing: '0.08em',
                    color: '#000',
                    display: 'block',
                  }}
                >
                  ROOTS <span style={{ color: AMPERSAND_COLOR }}>&</span> WINGS
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Manrope", sans-serif',
                    fontWeight: 300,
                    fontSize: 11,
                    lineHeight: '100%',
                    letterSpacing: '0.8em',
                    color: '#5a5a5a',
                    display: 'block',
                    mt: 0.5,
                  }}
                >
                  Foundation
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'center', pb: 1 }}>
              <StartCampaignHeaderCTA compact />
            </Box>
          </Box>
          {/* Top row - logo + name + contact (hidden on mobile) */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              gap: { xs: 2, sm: 2 },
              minHeight: { xs: 'auto', md: 85 },
              py: { xs: 2, md: 0 },
            }}
          >
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 10, sm: '14px' },
                textDecoration: 'none',
                color: 'inherit',
                flexShrink: 0,
              }}
            >
              {/* Figma: logo 47×50, left 30 top 35 */}
              <Box
                component="img"
                src={ASSETS.logoJpeg}
                alt="Roots & Wings"
                sx={{ width: { xs: 40, sm: 47 }, height: { xs: 43, sm: 50 }, objectFit: 'contain', display: 'block', flexShrink: 0 }}
              />
              <Box sx={{ minWidth: 0 }}>
                {/* Figma: ROOTS & WINGS - Manrope 800 17px, line-height 100%, letter-spacing 8%; & in brown */}
                <Typography
                  component="span"
                  sx={{
                    fontFamily: '"Manrope", sans-serif',
                    fontWeight: 800,
                    fontSize: { xs: 15, sm: 17 },
                    lineHeight: '100%',
                    letterSpacing: '0.08em',
                    color: '#000',
                    display: 'block',
                  }}
                >
                  ROOTS <span style={{ color: AMPERSAND_COLOR }}>&</span> WINGS
                </Typography>
                {/* Foundation - smaller, lighter weight; tighter letter-spacing on mobile for readability */}
                <Typography
                  sx={{
                    fontFamily: '"Manrope", sans-serif',
                    fontWeight: 300,
                    fontSize: { xs: 11, sm: 11 },
                    lineHeight: '100%',
                    letterSpacing: { xs: '0.35em', sm: '0.9em' },
                    color: '#5a5a5a',
                    display: 'block',
                    mt: 0.5,
                  }}
                >
                  Foundation
                </Typography>
              </Box>
            </Box>

            {/* Start Campaign + Helpline + email */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 1.5, sm: 2 },
                flexShrink: 0,
              }}
            >
              <StartCampaignHeaderCTA />
              <Box sx={{ width: 0, height: 38, borderLeft: '1px dashed #ADADAD', display: { xs: 'none', sm: 'block' }, flexShrink: 0 }} />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1.5, sm: 0 },
                }}
              >
                <Box sx={{ textAlign: 'left', pr: { xs: 0, sm: 1.5, md: 3 } }}>
                  <Typography
                    sx={{
                      fontFamily: '"Patrick Hand", cursive',
                      fontWeight: 400,
                      fontSize: { xs: 15, sm: 17 },
                      lineHeight: '100%',
                      letterSpacing: '2px',
                      color: '#707070',
                    }}
                  >
                    Helpline
                  </Typography>
                  <Typography
                    component="a"
                    href={`tel:${HELPLINE}`}
                    sx={{
                      fontFamily: '"Manrope", sans-serif',
                      fontWeight: 800,
                      fontSize: { xs: 14, sm: 14 },
                      lineHeight: '100%',
                      letterSpacing: '2px',
                      color: '#352F2F',
                      textDecoration: 'none',
                      display: 'block',
                      mt: 0.5,
                    }}
                  >
                    {HELPLINE}
                  </Typography>
                </Box>
                <Box sx={{ width: 0, height: 36, borderLeft: `1px solid ${SEPARATOR_GRAY}`, mx: { xs: 0, sm: 1, md: 2 }, display: { xs: 'none', sm: 'block' } }} />
                <Box sx={{ textAlign: 'left', pl: { xs: 0, sm: 0 } }}>
                  <Typography
                    sx={{
                      fontFamily: '"Patrick Hand", cursive',
                      fontWeight: 400,
                      fontSize: { xs: 15, sm: 17 },
                      lineHeight: '100%',
                      letterSpacing: '2px',
                      color: '#707070',
                    }}
                  >
                    Send email
                  </Typography>
                  <Typography
                    component="a"
                    href={`mailto:${EMAIL}`}
                    sx={{
                      fontFamily: '"Manrope", sans-serif',
                      fontWeight: 800,
                      fontSize: 14,
                      lineHeight: '100%',
                      letterSpacing: '2px',
                      color: '#352F2F',
                      textDecoration: 'none',
                      display: 'block',
                      mt: 0.5,
                      wordBreak: 'break-all',
                    }}
                  >
                    {EMAIL}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Figma: bottom strip - green left, brown right (hidden on mobile) */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, height: 4 }}>
            <Box sx={{ flex: 1, backgroundColor: MENU_GREEN }} />
            <Box sx={{ width: { xs: '40%', sm: 215 }, minWidth: { xs: 100, sm: 215 }, maxWidth: 215, backgroundColor: DONATE_BROWN }} />
          </Box>

          {/* Nav row - Figma: 1280×60px bar #477C2E; menu text Poppins 500 15px #FFF 3% letter-spacing; Donate 215×60px #8A4B1E */}
          <Box sx={{ display: 'flex', height: { xs: 56, md: 60 }, minWidth: 0, overflow: 'visible', position: 'relative', zIndex: 20 }}>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                backgroundColor: MENU_GREEN,
                display: 'flex',
                alignItems: 'center',
                overflow: 'visible',
              }}
            >
              <Toolbar disableGutters sx={{ minHeight: { xs: 56, md: 60 }, py: 0, width: '100%', minWidth: 0, overflow: 'visible' }}>
                <Box
                  onClick={() => setMobileOpen(true)}
                  sx={{
                    display: { md: 'none' },
                    mr: 1,
                    cursor: 'pointer',
                    p: 1,
                    color: '#fff',
                    flexShrink: 0,
                  }}
                  aria-label="Open menu"
                >
                  <Box sx={{ width: 24 }}>
                    <Box sx={{ height: 2, backgroundColor: 'currentColor', mb: 1 }} />
                    <Box sx={{ height: 2, backgroundColor: 'currentColor', mb: 1 }} />
                    <Box sx={{ height: 2, backgroundColor: 'currentColor' }} />
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    flexWrap: 'nowrap',
                    minWidth: 0,
                    flex: 1,
                    overflow: 'visible',
                    pr: { md: 1, lg: 1.5 },
                  }}
                >
                  {navLinks.map((item) => {
                    const { label, to, children } = item;
                    if (children) {
                      const childActive = children.some((child) => location.pathname === child.to);
                      return (
                        <Box key={label} sx={{ flexShrink: 0 }}>
                          <Box
                            ref={aboutRef}
                            onMouseEnter={openAboutMenu}
                            onMouseLeave={() => closeAboutMenu()}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              minHeight: 60,
                            }}
                          >
                            <Box
                              component={RouterLink}
                              to={to}
                              sx={{
                                fontFamily: '"Poppins", sans-serif',
                                fontWeight: 500,
                                fontSize: { md: 13, lg: 15 },
                                lineHeight: '100%',
                                letterSpacing: '0.03em',
                                color: '#FFFFFF',
                                textDecoration: 'none',
                                pl: { md: 1.25, lg: 2 },
                                pr: 0.25,
                                py: 0,
                                minHeight: 60,
                                display: 'flex',
                                alignItems: 'center',
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                                borderRadius: '10px 0 0 10px',
                                backgroundColor: aboutOpen || childActive ? 'rgba(255,255,255,0.14)' : 'transparent',
                                transition: 'background-color 0.2s ease',
                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.14)' },
                              }}
                            >
                              {label}
                            </Box>
                            <Box
                              component="button"
                              type="button"
                              aria-label="Open About menu"
                              aria-expanded={aboutOpen}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (aboutOpen) closeAboutMenu(true);
                                else openAboutMenu();
                              }}
                              sx={{
                                border: 'none',
                                backgroundColor: aboutOpen || childActive ? 'rgba(255,255,255,0.14)' : 'transparent',
                                color: '#fff',
                                cursor: 'pointer',
                                minHeight: 60,
                                pr: { md: 1, lg: 1.5 },
                                pl: 0.25,
                                display: 'flex',
                                alignItems: 'center',
                                borderRadius: '0 10px 10px 0',
                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.14)' },
                              }}
                            >
                              <KeyboardArrowDown sx={{ fontSize: 18, opacity: 0.9, transform: aboutOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }} />
                            </Box>
                          </Box>
                          <Popper
                            open={aboutOpen}
                            anchorEl={aboutRef.current}
                            placement="bottom-start"
                            disablePortal={false}
                            modifiers={[{ name: 'offset', options: { offset: [0, 0] } }]}
                            sx={{ zIndex: 1400 }}
                            onMouseEnter={openAboutMenu}
                            onMouseLeave={() => closeAboutMenu()}
                          >
                            <ClickAwayListener
                              onClickAway={(e) => {
                                if (aboutRef.current && aboutRef.current.contains(e.target)) return;
                                closeAboutMenu(true);
                              }}
                            >
                              <Paper
                                elevation={0}
                                sx={{
                                  minWidth: 260,
                                  backgroundColor: '#fff',
                                  borderRadius: '10px',
                                  boxShadow: '0 16px 40px rgba(0,0,0,0.16)',
                                  overflow: 'hidden',
                                  py: 0.75,
                                  mt: 0.5,
                                }}
                              >
                                {children.map((child) => (
                                  <Box
                                    key={child.to}
                                    component={RouterLink}
                                    to={child.to}
                                    onClick={() => closeAboutMenu(true)}
                                    sx={{
                                      display: 'block',
                                      px: 2,
                                      py: 1.15,
                                      fontFamily: '"Poppins", sans-serif',
                                      fontWeight: 500,
                                      fontSize: 14,
                                      color: location.pathname === child.to ? MENU_GREEN : '#1f2937',
                                      textDecoration: 'none',
                                      whiteSpace: 'nowrap',
                                      '&:hover': { backgroundColor: 'rgba(71, 124, 46, 0.08)', color: MENU_GREEN },
                                    }}
                                  >
                                    {child.label}
                                  </Box>
                                ))}
                              </Paper>
                            </ClickAwayListener>
                          </Popper>
                        </Box>
                      );
                    }
                    return (
                      <Box
                        key={label}
                        component={RouterLink}
                        to={to}
                        sx={{
                          fontFamily: '"Poppins", sans-serif',
                          fontWeight: 500,
                          fontSize: { md: 13, lg: 15 },
                          lineHeight: '100%',
                          letterSpacing: '0.03em',
                          color: '#FFFFFF',
                          textDecoration: 'none',
                          px: { md: 1.25, lg: 2 },
                          py: 0,
                          minHeight: 60,
                          display: 'flex',
                          alignItems: 'center',
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                          borderRadius: '10px',
                          transition: 'background-color 0.2s ease',
                          '&:hover': { backgroundColor: 'rgba(255,255,255,0.14)' },
                        }}
                      >
                        {label}
                      </Box>
                    );
                  })}
                  {featuredCampaigns.length > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                        ml: 0.5,
                        pl: 1,
                        borderLeft: '1px solid rgba(255,255,255,0.22)',
                        gap: 0.5,
                      }}
                    >
                      <AutoAwesome sx={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', mr: 0.25, display: { md: 'none', lg: 'block' } }} />
                      {featuredCampaigns.map((campaign) => (
                        <FeaturedNavLink key={campaign.id} campaign={campaign} />
                      ))}
                    </Box>
                  )}
                </Box>
              </Toolbar>
            </Box>
            <Box
              component="button"
              onClick={handleDonate}
              sx={{
                width: { xs: '40%', sm: 215 },
                minWidth: { xs: 100, sm: 215 },
                maxWidth: 215,
                height: { xs: 56, md: 60 },
                backgroundColor: DONATE_BROWN,
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                '&:hover': { backgroundColor: '#734019' },
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  color: '#FFFFFF',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                Donate Now
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ '& .MuiDrawer-paper': { width: 300, pt: 1.5, px: 1, pb: 2, bgcolor: '#fafaf8' } }}
      >
        <List disablePadding>
          {navLinks.map((item) => {
            const { label, to, children } = item;
            if (children) {
              return (
                <Box key={label}>
                  <ListItem disablePadding sx={{ mb: 0.25 }}>
                    <ListItemButton
                      onClick={() => setAboutMobileOpen((open) => !open)}
                      sx={{
                        borderRadius: 2,
                        py: 1.25,
                        '&:hover': { bgcolor: 'rgba(71, 124, 46, 0.08)' },
                      }}
                    >
                      <ListItemText
                        primary={label}
                        primaryTypographyProps={{
                          fontFamily: '"Poppins", sans-serif',
                          fontWeight: 600,
                          fontSize: 15,
                          letterSpacing: '0.02em',
                          color: '#1f2937',
                        }}
                      />
                      {aboutMobileOpen ? <ExpandLess sx={{ color: MENU_GREEN }} /> : <ExpandMore sx={{ color: '#667085' }} />}
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={aboutMobileOpen} timeout="auto" unmountOnExit>
                    <List disablePadding>
                      {children.map((child) => (
                        <ListItem key={child.to} disablePadding sx={{ mb: 0.25 }}>
                          <ListItemButton
                            component={RouterLink}
                            to={child.to}
                            onClick={() => setMobileOpen(false)}
                            sx={{
                              borderRadius: 2,
                              py: 1,
                              pl: 3.5,
                              bgcolor: location.pathname === child.to ? 'rgba(71, 124, 46, 0.08)' : 'transparent',
                              '&:hover': { bgcolor: 'rgba(71, 124, 46, 0.08)' },
                            }}
                          >
                            <ListItemText
                              primary={child.label}
                              primaryTypographyProps={{
                                fontFamily: '"Poppins", sans-serif',
                                fontWeight: 500,
                                fontSize: 14,
                                color: location.pathname === child.to ? MENU_GREEN : '#374151',
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              );
            }
            return (
              <ListItem key={label} disablePadding sx={{ mb: 0.25 }}>
                <ListItemButton
                  component={RouterLink}
                  to={to}
                  onClick={() => setMobileOpen(false)}
                  sx={{
                    borderRadius: 2,
                    py: 1.25,
                    '&:hover': { bgcolor: 'rgba(71, 124, 46, 0.08)' },
                  }}
                >
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                      fontFamily: '"Poppins", sans-serif',
                      fontWeight: 600,
                      fontSize: 15,
                      letterSpacing: '0.02em',
                      color: '#1f2937',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
          <ListItem disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              component={RouterLink}
              to="/campaigns/submit"
              onClick={() => setMobileOpen(false)}
              sx={{
                borderRadius: 2.5,
                py: 1.5,
                background: `linear-gradient(135deg, ${MENU_GREEN} 0%, #3d6a28 100%)`,
                color: '#fff',
                boxShadow: '0 8px 24px rgba(71, 124, 46, 0.25)',
                '&:hover': { bgcolor: '#3d6a28' },
              }}
            >
              <RocketLaunch sx={{ mr: 1.5, fontSize: 22 }} />
              <ListItemText
                primary="Start a Campaign"
                secondary="Submit your idea for review"
                primaryTypographyProps={{ fontWeight: 700, fontSize: 15 }}
                secondaryTypographyProps={{ fontSize: 12, color: 'rgba(255,255,255,0.85)' }}
              />
            </ListItemButton>
          </ListItem>
          <Divider sx={{ my: 1, borderColor: 'rgba(0,0,0,0.06)' }} />
          {featuredCampaigns.length > 0 && (
            <>
              <Divider sx={{ my: 1.5, borderColor: 'rgba(0,0,0,0.06)' }} />
              <ListSubheader
                disableSticky
                sx={{
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: '0.16em',
                  color: MENU_GREEN,
                  lineHeight: 1.5,
                  px: 1,
                  py: 0.5,
                  bgcolor: 'transparent',
                }}
              >
                Featured campaigns
              </ListSubheader>
              {featuredCampaigns.map((campaign) => {
                const isSchool = campaign.variant === 'school_kit';
                const accent = isSchool ? '#2d6a4f' : '#c2410c';
                const softBg = isSchool
                  ? 'linear-gradient(120deg, #ecfdf5 0%, #d8f3dc 55%, #f0fdf4 100%)'
                  : 'linear-gradient(120deg, #fff7ed 0%, #ffedd5 50%, #fffbeb 100%)';

                return (
                  <ListItem key={`mobile-${campaign.id}`} disablePadding sx={{ mb: 1 }}>
                    <ListItemButton
                      component={RouterLink}
                      to={getCampaignPath(campaign.slug)}
                      onClick={() => setMobileOpen(false)}
                      sx={{
                        borderRadius: 2.5,
                        py: 1.5,
                        px: 1.5,
                        background: softBg,
                        border: '1px solid rgba(0,0,0,0.06)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 12px 28px rgba(0,0,0,0.1)',
                          transform: 'translateY(-1px)',
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: 4,
                          bgcolor: accent,
                          borderRadius: '4px 0 0 4px',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 0.5 }}>
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(255,255,255,0.75)',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                            flexShrink: 0,
                          }}
                        >
                          {isSchool ? (
                            <ShoppingBag sx={{ fontSize: 22, color: accent }} />
                          ) : (
                            <Fastfood sx={{ fontSize: 22, color: accent }} />
                          )}
                        </Box>
                        <ListItemText
                          primary={campaign.title}
                          secondary={isSchool ? 'School kit · tap to donate' : 'Hunger relief · tap to donate'}
                          primaryTypographyProps={{
                            fontFamily: '"Poppins", sans-serif',
                            fontWeight: 700,
                            fontSize: 14.5,
                            letterSpacing: '0.01em',
                            color: '#111827',
                            lineHeight: 1.3,
                          }}
                          secondaryTypographyProps={{
                            fontFamily: '"Poppins", sans-serif',
                            fontSize: 12,
                            color: '#6b7280',
                            mt: 0.35,
                          }}
                        />
                      </Box>
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </>
          )}
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => { handleDonate(); setMobileOpen(false); }}
              sx={{ backgroundColor: DONATE_BROWN, color: 'white' }}
            >
              <ListItemText primary="DONATE NOW" primaryTypographyProps={{ fontWeight: 700 }} />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
    </>
  );
};

export default Header;
